// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'

// swagger-ui
import SwaggerUI from 'swagger-ui'
import 'swagger-ui/dist/swagger-ui.css'

// semantic-ui
import { Container, Header, Icon } from 'semantic-ui-react'

// services
import { isRegistered } from 'services/self'
import { updateUsagePlansAndApisList, getApi } from 'services/api-catalog'

// components
import ApisMenu from 'components/ApisMenu'
import SwaggerLayoutPlugin from 'components/SwaggerUiLayout'
import PageWithSidebar from 'components/PageWithSidebar'

// state
import { store } from 'services/state.js'
import { observer } from 'mobx-react'

// Plugin that blocks the actual HTTP request (executeRequest) and hides
// the response output section entirely — only the curl command is shown.
const CurlOnlyPlugin = () => ({
  wrapActions: {
    executeRequest: (oriAction) => (_args) => {
      // Intentionally do nothing — this stops the real HTTP call.
      // The curl command is built during the earlier `execute` action
      // and will still be displayed in the UI.
    }
  },
  wrapComponents: {
    // Hide the "Server response" / live response panel completely.
    LiveResponse: () => () => null,
    // Hide the "Authorize" button completely.
    authorizeBtn: () => () => null
  }
})

export default observer(class ApisPage extends React.Component {
  containerRef = React.createRef()
  hasRoot = false

  componentDidMount () { this.updateApi(true) }
  componentDidUpdate () { this.updateApi(false) }
  componentWillUnmount () { this.containerRef = null }

  updateApi (isInitial) {
    return getApi(this.props.match.params.apiId || 'ANY', true, this.props.match.params.stage, isInitial)
      .then(api => {
        if (this.containerRef == null) return
        const elem = this.containerRef.current
        const isFirstLoad = !this.hasRoot

        this.hasRoot = elem != null
        if (api && elem != null) {
          let filteredSpec = api.swagger
          if (filteredSpec && filteredSpec.paths) {
            const {
              '/fetchActiveZipCode': _1,
              '/login': _2,
              '/verify': _3,
              '/shipment/{tracking_number}': _4,
              ...remainingPaths
            } = filteredSpec.paths

            const orderedPaths = {}
            const desiredOrder = [
              '/rateShipment',
              '/createShipment',
              '/trackShipment',
              '/voidShipment',
              '/voidPackage',
              '/webhooks'
            ]

            desiredOrder.forEach(path => {
              if (remainingPaths[path]) {
                orderedPaths[path] = remainingPaths[path]
                delete remainingPaths[path]
              }
            })

            Object.assign(orderedPaths, remainingPaths)
            filteredSpec = { ...filteredSpec, paths: orderedPaths }
          }

          const swaggerUiConfig = {
            domNode: this.containerRef.current,
            plugins: [SwaggerLayoutPlugin, CurlOnlyPlugin],
            // Always allow Try It Out (so curl is generated) but
            // CurlOnlyPlugin blocks the actual HTTP execute call.
            spec: filteredSpec,
            onComplete: () => {
              uiHandler.preauthorizeApiKey('api_key', 'YOUR_API_KEY_HERE')
            }
          }

          const uiHandler = SwaggerUI(swaggerUiConfig)

          if (isFirstLoad) return updateUsagePlansAndApisList(true)
        }
      })
  }

  render () {
    let errorHeader
    let errorBody

    if (store.apiList.loaded) {
      if (!store.apiList.apiGateway.length && !store.apiList.generic.length) {
        errorHeader = 'No APIs Published'
        errorBody = 'Your administrator hasn\'t added any APIs to your account. Please contact them to publish an API.'
      } else if (!store.api) {
        errorHeader = 'No Such API'
        errorBody = 'The selected API doesn\'t exist.'
      }
    }

    return (
      <PageWithSidebar
        sidebarContent={<ApisMenu path={this.props.match} activateFirst={true} />}
        SidebarPusherProps={{ className: 'swagger-section' }}
      >
        <div className='swagger-ui-wrap' ref={this.containerRef} style={{ padding: '0 20px' }}>
          {errorHeader && errorBody && (
            <>
              <Header as='h2' icon textAlign='center' style={{ padding: '40px 0px' }}>
                <Icon name='warning sign' circular />
                <Header.Content>{errorHeader}</Header.Content>
              </Header>
              <Container text textAlign='justified'>
                <p>{errorBody}</p>
              </Container>
            </>
          )}
        </div>
      </PageWithSidebar>
    )
  }
})
